import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import AnswerList from 'components/customer/answer_panel/search/answer_list';
import AnswerPanelContext from 'components/customer/answer_panel/answer_panel_context';
import { HEIGHT as ROW_HEIGHT } from 'components/customer/answer_panel/search/answer_list_item';
import LoadMoreAnswerSearchResults from 'actions/answers/load_more_answer_search_results';
import ScrollNotifier from 'components/lib/scroll_notifier';
import Spinner from 'components/common/spinner';
import useExecuteAction from 'components/hooks/use_execute_action';

// Trigger the next page when the scroll position is within ~2 list-row heights of the bottom.
const SCROLL_THRESHOLD = 64;

export default function AnswerSearchResults() {
  const { t } = useTranslation();
  const { answersList, focusedAnswer, isLoadingMoreSearchResults, searchText } = useContext(AnswerPanelContext);
  const executeAction = useExecuteAction();
  const [scrollContainer, setScrollContainer] = useState(null);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const answerCount = answersList.answers.length;

  const updateCanScrollDown = useCallback(() => {
    if (!scrollContainer) return;
    const { scrollTop, clientHeight, scrollHeight } = scrollContainer;
    setCanScrollDown(scrollTop + clientHeight < scrollHeight - 1);
  }, [scrollContainer]);

  const handleScroll = () => {
    if (!scrollContainer) return;
    const { scrollTop, clientHeight, scrollHeight } = scrollContainer;
    if (!isLoadingMoreSearchResults && scrollTop + clientHeight >= scrollHeight - SCROLL_THRESHOLD) {
      executeAction(LoadMoreAnswerSearchResults);
    }
    updateCanScrollDown();
  };

  const scrollToMore = () => {
    if (!scrollContainer) return;
    // Scroll a page, keeping one row of overlap for continuity.
    const top = Math.max(scrollContainer.clientHeight - ROW_HEIGHT, ROW_HEIGHT);
    scrollContainer.scrollBy({ top, behavior: 'smooth' });
  };

  // Keep the fade in sync as the panel resizes (resizing fires no scroll event).
  useEffect(() => {
    if (!scrollContainer || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(updateCanScrollDown);
    observer.observe(scrollContainer);
    return () => observer.disconnect();
  }, [updateCanScrollDown, scrollContainer]);

  // Re-measure when results load in (content height changes without a scroll or resize event).
  useEffect(updateCanScrollDown, [updateCanScrollDown, answerCount, isLoadingMoreSearchResults]);

  return (
    <ResultsViewport>
      <Results data-aid="answerPanel-searchResults" ref={setScrollContainer}>
        <ScrollNotifier onScroll={handleScroll} scrollingParent={scrollContainer}>
          <React.Fragment>
            <AnswerList
              answers={answersList.answers}
              searchText={searchText}
              selectedAnswerId={focusedAnswer && focusedAnswer.id}
            />
            {isLoadingMoreSearchResults && (
              <LoadingMoreRow data-aid="answerPanel-loadingMore">
                <Spinner size={24} />
              </LoadingMoreRow>
            )}
          </React.Fragment>
        </ScrollNotifier>
      </Results>
      <ScrollMore data-aid="answerPanel-scrollMore" visible={canScrollDown}>
        <ScrollMoreButton
          aria-label={t('Scroll down for more results')}
          data-aid="answerPanel-scrollMoreButton"
          disabled={!canScrollDown}
          onClick={scrollToMore}
          type="button"
        >
          <FontAwesomeIcon icon={faChevronDown} />
        </ScrollMoreButton>
      </ScrollMore>
    </ResultsViewport>
  );
}

const ResultsViewport = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
`;

const Results = styled.div`
  height: 100%;
  overflow: auto;
`;

const ScrollMore = styled.div`
  align-items: flex-end;
  background: linear-gradient(to bottom, transparent, ${p => p.theme.colors.white} 55%);
  bottom: 0;
  display: flex;
  height: ${p => p.theme.spacing.xlarge};
  justify-content: center;
  left: 0;
  opacity: ${p => (p.visible ? 1 : 0)};
  pointer-events: none;
  position: absolute;
  right: 0;
  transition: opacity ${p => p.theme.transitions.fast};
`;

const ScrollMoreButton = styled.button`
  background: none;
  border: 0;
  color: ${p => p.theme.colors.gray600};
  cursor: pointer;
  font-size: ${p => p.theme.fontSize.small};
  line-height: 1;
  padding: ${p => p.theme.spacing.xsmall} ${p => p.theme.spacing.small};
  pointer-events: auto;

  &:hover {
    color: ${p => p.theme.colors.green400};
  }
`;

const LoadingMoreRow = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  padding: 12px 0;
`;
